import React from 'react';
import './plan_details_table.css';
import PropTypes from 'prop-types';

function PlanDetailsTable({
  plans,
  selectable,
  currentPlanId,
  selectedPlanId,
  onSelectPlan,
}) {
  if (!plans.length) {
    return <div className="text-muted">No plans available</div>;
  }

  const sortedPlans = [...plans]
    .filter((p) => p.level > 0)
    .filter((p) => !(selectable && p.id === 1))
    .sort((a, b) => a.level - b.level);

  // Highlight logic only applies in selectable mode
  let activeIndex = -1;
  let isRecommended = false;
  if (selectable) {
    if (currentPlanId === 1) {
      // Recommend the 2nd visible tier
      if (sortedPlans.length >= 2) {
        activeIndex = 1;
        isRecommended = true;
      }
    } else {
      const highlightId = selectedPlanId || currentPlanId;
      activeIndex = sortedPlans.findIndex(
        (p) => String(p.id) === String(highlightId),
      );
    }
  }

  const renderLimit = (limit) => {
    if (limit === 0) return '—';
    if (limit === 1000000) return 'Unlimited';
    return limit;
  };

  const featureRows = [
    { label: 'Registered Devices', render: (p) => (p.deviceLimit) },
    { label: 'Users', render: (p) => renderLimit(p.userLimit) },
    { label: 'User Roles', render: (p) => (p.supportsRoles ? '✓' : '—') },
    { label: 'Configured Alerts', render: (p) => renderLimit(p.alertLimit) },
    { label: 'Reports', render: (p) => renderLimit(p.reportLimit) },
    { label: 'Monitor360 Sensors', render: (p) => (p.id !== 1 ? 'Unlimited' : '—') },
    { label: 'Registered Links', render: (p) => (p.id !== 1 ? 'Unlimited' : '—') },
    {
      label: 'Data History',
      render: (p) => `${p.dataAgeLimitValue} ${p.dataAgeLimitUnit}${
        p.dataAgeLimitValue > 1 ? 's' : ''
      }`,
    },
    { label: 'Room Alert Manager', render: (p) => (p.id !== 1 ? '✓' : '—') },
    { label: 'SAML', render: (p) => (p.level > 4 ? '✓' : '—') },
  ];

  const handleSelect = (plan) => {
    if (onSelectPlan) onSelectPlan(plan);
  };

  return (
    <div className="container-fluid py-4">
      <table className="table table-striped text-center align-middle pricing-table">
        <thead>
          <tr>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th />

            {sortedPlans.map((plan, i) => {
              const isActive = i === activeIndex;
              const isCurrent = selectable && String(plan.id) === String(currentPlanId);
              const monthlyAvailable = Boolean(plan.monthlyCrmProductId)
              && parseFloat(plan.monthlyPrice) >= 1;

              return (
                <th
                  key={plan.id}
                  className={isActive ? 'active-col position-relative' : ''}
                >
                  {isActive && (
                  <div className="current-label">
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {selectedPlanId
                      ? 'SELECTED'
                      : (isRecommended ? 'RECOMMENDED' : 'CURRENT LEVEL')}
                  </div>
                  )}

                  <div className="fw-bold mt-3">{plan.name}</div>

                  <div className="mt-2 mb-0" style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
                    $
                    {(parseFloat(plan.yearlyPrice) / 12).toFixed(2)}
                  </div>
                  <div className="small text-muted">per month billed annually</div>

                  <>
                    <div className={`small my-1 ${monthlyAvailable ? '' : 'invisible'}`}>or</div>

                    <div className={`h5 fw-bold mb-2 ${monthlyAvailable ? '' : 'invisible'}`}>
                      {monthlyAvailable ? (
                        <>
                          $
                          {parseFloat(plan.monthlyPrice).toFixed(2)}
                          /mo
                        </>
                      ) : (
                        <>&nbsp;</>
                      )}
                    </div>
                  </>

                  {selectable && (
                  <button
                    type="button"
                    className={`btn btn-sm mt-2 ${isCurrent ? 'btn-secondary' : 'btn-primary'}`}
                    disabled={isCurrent}
                    onClick={() => handleSelect(plan)}
                  >
                    {isCurrent ? 'Current Plan' : 'Select'}
                  </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {featureRows.map((feature, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              <td className="text-start fw-semibold">{feature.label}</td>

              {sortedPlans.map((plan, colIndex) => (
                <td
                  key={plan.id}
                  className={colIndex === activeIndex ? 'active-col' : ''}
                >
                  {feature.render(plan)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

PlanDetailsTable.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectable: PropTypes.bool,
  currentPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectPlan: PropTypes.func,
};

PlanDetailsTable.defaultProps = {
  selectable: true,
  currentPlanId: null,
  selectedPlanId: null,
  onSelectPlan: null,
};

export default PlanDetailsTable;
